/**
 * Persisting a chat workflow negotiation to its server draft (MAR-437 follow-up). Composed by the
 * `web-chat` store facade (MAR-948).
 *
 * **`postWorkflowDraft` is the one `/draft` request in the app.** The debounced live-edit flush and
 * the card's confirm button used to build the request separately — one through `apiFetch`, one
 * through `authFetch` — each spelling out the scope fields by hand. Both go through here now, so the
 * run a draft files into (MAR-448 case, AlexAmin/marcus#206 client) is decided once.
 *
 * Nothing is posted for a proposal the user has not confirmed (MAR-988): see `queueWorkflowDraftSync`.
 *
 * Every live edit on a CONFIRMED negotiation — an inline field edit or a party-fill pick, from the
 * chat card OR the context rail, since both call `syncWorkflowField` — is buffered and flushed as one
 * debounced POST, so typing a name does not regenerate the document per keystroke. One flush chain,
 * which the confirm button also awaits before its own request, so a flush and a confirm never race
 * client-side. The server's `pg_advisory_xact_lock` (`upsertWorkflowDraft`) is what makes two flushes
 * that DO overlap safe rather than a lost update.
 */
import type { Ref } from 'vue'
import type { ApiFetch } from '~/composables/useApi'
import type { WorkflowRequest } from '~/types/web-chat'
import type { WorkflowDraftRequest, WorkflowDraftResult } from '~~/shared/workflows/workflowRun'
import type { WorkflowProposal, WorkflowProposalStatus } from '~~/shared/workflows/workflowProposal'

export interface WorkflowDraftSyncDeps {
  conversationId: Ref<string | null>
  /** The chat's scope, read when a request is SENT — never captured when it was queued. */
  activeCaseId: Ref<string | null>
  activeClientId: Ref<string | null>
  apiFetch: ApiFetch
  /** A flush regenerated `itemId` — the open preview re-fetches if it is showing it. */
  onDocumentRegenerated: (itemId: string | null) => void
}

/** What a caller decides; the scope is filled in here. */
export type WorkflowDraftPost = Omit<WorkflowDraftRequest, 'caseId' | 'clientId'>

const DRAFT_FLUSH_DEBOUNCE_MS = 600

export function useWorkflowDraftSync(deps: WorkflowDraftSyncDeps) {
  const { conversationId, activeCaseId, activeClientId, apiFetch, onDocumentRegenerated } = deps

  function postWorkflowDraft(workflowId: string, body: WorkflowDraftPost): Promise<WorkflowDraftResult> {
    return apiFetch<WorkflowDraftResult>(`/api/workflows/${workflowId}/draft`, {
      method: 'POST',
      body: {
        ...body,
        // Threads the chat's scope into the run so its output files into that case or client
        // instead of the org-wide "not in a case" bucket. One is always null: one rung at a time.
        caseId: activeCaseId.value,
        clientId: activeClientId.value,
      } satisfies WorkflowDraftRequest,
    })
  }

  const pendingDraftPatch: Record<string, unknown> = {}
  /** The request the pending patch belongs to — set by whichever call queued it last. There is only
   *  ever one open negotiation in practice, so "last call wins" is exact, not a simplification. */
  let pendingFlushRequest: WorkflowRequest | null = null
  let draftFlushTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * Every flush chains onto this rather than racing it — one outgoing `/draft` from this tab at a
   * time, in QUEUE order, each reading `pendingDraftPatch` fresh when its turn comes. That is what
   * makes `await flushWorkflowDraft()` after an immediate queue a real guarantee.
   */
  let draftFlushChain: Promise<void> = Promise.resolve()

  async function doWorkflowDraftFlush(): Promise<void> {
    const request = pendingFlushRequest
    const conversation = conversationId.value
    if (!request || !conversation) return
    const toSend = { ...pendingDraftPatch }
    for (const k in toSend) delete pendingDraftPatch[k]
    if (Object.keys(toSend).length === 0) return

    const workflowIdAtFlush = request.workflowId
    try {
      const result = await postWorkflowDraft(workflowIdAtFlush, {
        values: toSend,
        conversationId: conversation,
        conversationMessageId: request.conversationMessageId,
      })
      // Reconcile with the SERVER's persisted state, not just this flush's patch — a concurrent edit
      // from another tab may have set something this call never sent. Only fields THIS card declares;
      // extra keys in `inputValues` (`template`, `__outputs`) are not fields.
      if (request.workflowId === workflowIdAtFlush) {
        request.draftRunId = result.runId
        request.itemId = result.itemId
        for (const field of request.fields) {
          if (result.inputValues[field.key] === undefined) continue
          field.value = result.inputValues[field.key]
          field.prefilled = true
        }
      }
      onDocumentRegenerated(result.itemId)
    } catch (err) {
      console.warn('[webChatStore] Could not sync workflow draft edit:', err)
    }
  }

  /** Append a flush to the queue and return it — awaiting it waits for every link queued before. */
  function flushWorkflowDraft(): Promise<void> {
    if (draftFlushTimer) {
      clearTimeout(draftFlushTimer)
      draftFlushTimer = null
    }
    draftFlushChain = draftFlushChain.then(doWorkflowDraftFlush, doWorkflowDraftFlush)
    return draftFlushChain
  }

  /**
   * Queue a patch for a card's negotiation. `immediate` skips the debounce.
   *
   * ONLY once the negotiation has a document (MAR-988, CHAT-2.07): a `/draft` renders a real `.docx`
   * into the library, and none may exist before the user confirms ("Generar borrador"/"Ejecutar").
   * `draftRunId` is set by that confirm (`acceptWorkflow`) or by a card rebuilt from its run after a
   * reload — never by a proposal merely appearing. Before it, an edit lives in `request.fields`,
   * which is exactly what the confirm sends (`collectValues`), and is saved onto the proposal's tool
   * call (`saveWorkflowProposal`) so it survives a reload — still with no document.
   */
  function queueWorkflowDraftSync(request: WorkflowRequest, patch: Record<string, unknown>, opts: { immediate?: boolean } = {}) {
    if (!request.draftRunId) return
    pendingFlushRequest = request
    Object.assign(pendingDraftPatch, patch)
    if (draftFlushTimer) clearTimeout(draftFlushTimer)
    if (opts.immediate) void flushWorkflowDraft()
    else draftFlushTimer = setTimeout(() => { void flushWorkflowDraft() }, DRAFT_FLUSH_DEBOUNCE_MS)
  }

  /** Write locally AND queue the persisted sync — what both the card's and the rail's value proxies
   *  call, so neither surface can write a value the server never learns about. */
  function syncWorkflowField(request: WorkflowRequest, key: string, value: unknown) {
    const field = request.fields.find(f => f.key === key)
    if (field) {
      field.value = value
      field.prefilled = value !== undefined && value !== null && value !== ''
    }
    if (request.draftRunId) queueWorkflowDraftSync(request, { [key]: value })
    else queueWorkflowProposalSave(request)
  }

  // ── The UNCONFIRMED proposal (MAR-988) ─────────────────────────────────────────────────────────
  //
  // No run, no document: the card's whole state is saved onto the `run_workflow` tool call it came
  // from, which is what a reload rebuilds it from (`confirmCardRehydration.ts`). Debounced like the
  // draft flush, so typing a name is one save, not one per keystroke.
  let proposalSaveTimer: ReturnType<typeof setTimeout> | null = null
  let proposalSaveRequest: WorkflowRequest | null = null

  /**
   * Save an unconfirmed proposal NOW: its current fields, and whether it was declined. Best-effort —
   * a failed save costs the edit only across a reload, never the edit on screen or the confirm.
   */
  async function saveWorkflowProposal(request: WorkflowRequest, status: WorkflowProposalStatus = 'pending'): Promise<void> {
    if (proposalSaveRequest === request && proposalSaveTimer) {
      clearTimeout(proposalSaveTimer)
      proposalSaveTimer = null
    }
    const conversation = conversationId.value
    if (!conversation || !request.proposalCallId) return
    const values: Record<string, unknown> = {}
    for (const field of request.fields) if (field.value !== undefined) values[field.key] = field.value
    try {
      await apiFetch(`/api/conversations/${conversation}/workflow-proposals/${encodeURIComponent(request.proposalCallId)}`, {
        method: 'PUT',
        body: { values, status } satisfies WorkflowProposal,
      })
    } catch (err) {
      console.warn('[webChatStore] Could not save the workflow proposal:', err)
    }
  }

  function queueWorkflowProposalSave(request: WorkflowRequest) {
    if (proposalSaveTimer) clearTimeout(proposalSaveTimer)
    proposalSaveRequest = request
    proposalSaveTimer = setTimeout(() => {
      proposalSaveTimer = null
      void saveWorkflowProposal(request)
    }, DRAFT_FLUSH_DEBOUNCE_MS)
  }

  return { postWorkflowDraft, flushWorkflowDraft, queueWorkflowDraftSync, syncWorkflowField, saveWorkflowProposal }
}
